import {
  AutomationExecution,
  canPrepareAutomationExecutionCommunication,
  getAutomationExecutionStatusName,
  needsCommunicationPublishing,
} from "./automationExecutionRules";

/** An execution as the service sends it, in the camelCase the pages read. */
export function mapAutomationExecutionSummary(execution: AutomationExecution) {
  const relatedEntityType = execution?.RelatedEntityType || "";
  const fixtureExecution = relatedEntityType === "fixture";
  const communicationId = fixtureExecution
    ? execution?.ExecutionCommunicationId
    : execution?.CommunicationId;
  const communicationName = fixtureExecution
    ? execution?.ExecutionCommunicationName
    : execution?.CommunicationName;

  return {
    automationId: execution?.AutomationId || "",
    automationName: execution?.AutomationName || "",
    automationStatus: execution?.AutomationStatus,
    executionAutomationStatus: execution?.ExecutionAutomationStatus,
    automationType: execution?.AutomationType || "",
    audienceId: execution?.AudienceId || "",
    audienceName: execution?.AudienceName || "",
    automationTestMarketingListId: execution?.AutomationTestMarketingListId || "",
    automationTestMarketingListName: execution?.AutomationTestMarketingListName || "",
    canCancel: Boolean(execution?.CanCancel),
    communicationId: communicationId || "",
    communicationName: communicationName || "",
    competitionName: execution?.CompetitionName || "",
    completedOn: execution?.CompletedOn || "",
    contentPreparationStartsAt: execution?.ContentPreparationStartsAt || "",
    dueAt: execution?.DueAt || "",
    emailsSent: execution?.EmailsSent,
    openRate: execution?.OpenRate,
    clickRate: execution?.ClickRate,
    clicksToOpenRate: execution?.ClicksToOpenRate,
    executionId: execution?.ExecutionId || "",
    executionCommunicationStatusCode:
      execution?.ExecutionCommunicationStatusCode ||
      execution?.CommunicationStatusCode,
    fixtureId: fixtureExecution ? execution?.RelatedEntityId || "" : "",
    fixtureName: execution?.FixtureName || execution?.RelatedEntityName || "",
    lastError: execution?.LastError || "",
    occurrenceKey: execution?.OccurrenceKey || "",
    processingWindowStartsAt: execution?.ProcessingWindowStartsAt || "",
    relatedEntityId: execution?.RelatedEntityId || "",
    relatedEntityName: execution?.RelatedEntityName || "",
    relatedEntityType,
    scheduleMode: execution?.ScheduleMode || "",
    status: execution?.Status,
    statusLabel: execution?.StatusLabel || getAutomationExecutionStatusName(execution),
    statusName: getAutomationExecutionStatusName(execution),
    teamName: execution?.TeamName || "",
  };
}

/** The automation's executions as related-table rows: an id, a name and the fields. */
export function mapAutomationExecutionTableRecords(executions: unknown) {
  const records: AutomationExecution[] = Array.isArray(executions)
    ? executions
    : (executions as { Records?: AutomationExecution[] })?.Records || [];

  return records.map((execution) => {
    const mappedExecution = mapAutomationExecutionSummary(execution);
    const requiresPublishing = needsCommunicationPublishing(mappedExecution);

    return {
      Id: mappedExecution.executionId,
      Name: mappedExecution.dueAt || mappedExecution.executionId,
      Fields: {
        dueAt: mappedExecution.dueAt,
        processingWindowStartsAt: mappedExecution.processingWindowStartsAt,
        contentPreparationStartsAt: mappedExecution.contentPreparationStartsAt,
        automationId: mappedExecution.automationId,
        automationStatus: mappedExecution.automationStatus,
        executionAutomationStatus: mappedExecution.executionAutomationStatus,
        relatedEntityType: mappedExecution.relatedEntityType,
        status: mappedExecution.status,
        statusName: mappedExecution.statusName,
        statusLabel: mappedExecution.statusLabel,
        needsCommunicationPublishing: requiresPublishing,
        completedOn: mappedExecution.completedOn,
        communicationId: mappedExecution.communicationId,
        communicationName: mappedExecution.communicationName || "--",
        executionCommunicationStatusCode: mappedExecution.executionCommunicationStatusCode,
        updateCommunicationLink:
          requiresPublishing && mappedExecution.communicationId
            ? {
                to: `/email-communication?id=${encodeURIComponent(
                  mappedExecution.communicationId
                )}`,
              }
            : null,
        canPrepare: canPrepareAutomationExecutionCommunication(mappedExecution),
        id: mappedExecution.executionId,
        audienceId: mappedExecution.audienceId,
        audienceName: mappedExecution.audienceName || "--",
        automationTestMarketingListId: mappedExecution.automationTestMarketingListId,
        automationTestMarketingListName:
          mappedExecution.automationTestMarketingListName || "--",
        emailsSent: mappedExecution.emailsSent,
        openRate: mappedExecution.openRate,
        clickRate: mappedExecution.clickRate,
        clicksToOpenRate: mappedExecution.clicksToOpenRate,
        fixtureId: mappedExecution.fixtureId,
        fixtureName: mappedExecution.relatedEntityName || "--",
        lastError: mappedExecution.lastError || "--",
      },
    };
  });
}
